import { useState, useEffect } from "react";
import { Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import axios from "axios";

const API_URL = "https://backend.aktcorp.com/relateddocuments";

const DeleteDocument = () => {
    const [pdfs, setPdfs] = useState([]);
    const [deletePdfId, setDeletePdfId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [deleteSuccess, setDeleteSuccess] = useState(false);
    const [showPage, setShowPage] = useState(false);

    const jwtToken = localStorage.getItem("authToken");

    useEffect(() => {
        const timeout = setTimeout(() => setShowPage(true), 2000);
        return () => clearTimeout(timeout);
    }, []);

    const fetchPdfs = async () => {
        try {
            const response = await axios.get(API_URL, {
                headers: {
                    Authorization: `Bearer ${jwtToken}`,
                },
            });
            setPdfs(response.data.data);
        } catch (error) {
            console.error("Error fetching documents:", error);
            toast.error("Failed to load documents");
        }
    };

    useEffect(() => {
        fetchPdfs();
    }, [jwtToken]);

    const handleDelete = async () => {
        if (!deletePdfId) return;
        setLoading(true);
        setDeleteSuccess(false);

        try {
            await axios.delete(`${API_URL}/${deletePdfId}`, {
                headers: {
                    Authorization: `Bearer ${jwtToken}`,
                },
            });

            setDeleteSuccess(true);
            setPdfs((prev) => prev.filter((pdf) => pdf.id !== deletePdfId));

            setTimeout(() => {
                setDeletePdfId(null);
                setDeleteSuccess(false);
            }, 1500);
        } catch (error) {
            console.error(error);
            toast.error("Failed to delete document");
        } finally {
            setLoading(false);
        }
    };

    if (!showPage) {
        return (
            <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                className="w-24 h-24 border-4 border-t-transparent border-blue-600 rounded-full mx-auto mt-32"
            ></motion.div>
        );
    }

    return (
        <div className="p-6 max-w-4xl mx-auto bg-white text-gray-700 rounded-lg mb-7">
            <motion.h2
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-3xl font-bold text-gray-900 bg-clip-text text-center mb-4 text-transparent bg-gradient-to-r from-blue-500 to-purple-500"
            >
                Delete Related Document
            </motion.h2>

            <ul className="space-y-3">
                {pdfs.length > 0 ? (
                    pdfs.map((pdf) => (
                        <li
                            key={pdf.id}
                            className="flex items-center justify-between bg-gray-700 p-4 rounded-lg shadow-md hover:bg-gray-800 transition"
                        >
                            <div className="flex-1">
                                <h3 className="font-semibold text-lg text-white">{pdf.title}</h3>
                                <p className="text-sm text-white">Category: {pdf.category}</p>
                                <p className="text-sm text-white">File Type: {pdf.fileType}</p>
                                <p className="text-sm text-white">Language: {pdf.language}</p>
                            </div>

                            <button
                                onClick={() => setDeletePdfId(pdf.id)}
                                className="p-2 bg-red-500 rounded-full text-white hover:bg-red-600 transition"
                            >
                                <Trash2 size={20} />
                            </button>
                        </li>
                    ))
                ) : (
                    <p className="text-center text-gray-500">No documents available.</p>
                )}
            </ul>

            {/* Delete Confirmation Popup */}
            {deletePdfId && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg relative">
                        <motion.h2
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="text-2xl font-bold text-gray-900 bg-clip-text text-center mb-4 text-transparent bg-gradient-to-r from-blue-500 to-purple-500"
                        >
                            Confirm Delete
                        </motion.h2>

                        {loading ? (
                            <div className="flex justify-center items-center py-4">
                                <div className="w-10 h-10 border-4 border-t-red-500 border-b-transparent border-r-transparent border-l-blue-500 rounded-full animate-spin"></div>
                            </div>
                        ) : deleteSuccess ? (
                            <p className="text-center text-green-600 font-semibold">Document deleted successfully!</p>
                        ) : (
                            <>
                                <p className="text-center text-red-600">Are you sure you want to delete this Document?</p>
                                <div className="flex justify-around mt-4">
                                    <button
                                        onClick={handleDelete}
                                        className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition"
                                    >
                                        Yes
                                    </button>
                                    <button
                                        onClick={() => setDeletePdfId(null)}
                                        className="bg-gray-400 text-white px-4 py-2 rounded-lg hover:bg-gray-500 transition"
                                    >
                                        No
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeleteDocument;
